import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Select, Label } from "@/admin/ui";
import { toast } from "sonner";
import { Sparkles, Loader2, Send } from "lucide-react";

const NewsletterAdmin = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [template, setTemplate] = useState("editorial");
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [tone, setTone] = useState("warm");
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    api.get("/admin/newsletter").then(({ data }) => {
      const subs = (data || []).filter((s) => s.subscribed !== false);
      setCount(subs.length);
    }).catch(() => setCount(0));
  }, []);

  const draft = async () => {
    if (!aiPrompt.trim()) { toast("Tell the assistant what to write about."); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/ai/email-compose", { instruction: aiPrompt, tone });
      setMessage(data.reply || "");
      toast.success("Draft ready — review & edit before sending");
    } catch { toast.error("Could not draft"); }
    setDrafting(false);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast("Add a subject and a message first."); return; }
    if (!window.confirm(`Send this newsletter to ${count ?? "all"} subscriber(s)? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, message, template });
      toast.success(`Newsletter queued to ${data.queued} subscriber(s).`);
      setSubject(""); setMessage(""); setAiPrompt("");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not send");
    }
    setSending(false);
  };

  return (
    <div data-testid="newsletter-admin">
      <PageHead title="Newsletter" sub={count == null ? "Broadcast to your subscribers" : `${count} subscriber${count === 1 ? "" : "s"} on your list`}>
        <Btn onClick={send} disabled={sending} testid="newsletter-send">
          {sending ? <span className="flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Sending…</span>
                   : <span className="flex items-center gap-2"><Send size={13} /> Send newsletter</span>}
        </Btn>
      </PageHead>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <Card>
          <Field label="Subject line" value={subject} onChange={setSubject} placeholder="e.g. Ori Zuri arrives 12 October" testid="newsletter-subject" />
          <Field label="Message" value={message} onChange={setMessage} textarea placeholder="Write your message. Line breaks are kept. A branded header, footer and unsubscribe link are added automatically." testid="newsletter-message" />
          <Select label="Template / vibe" value={template} onChange={setTemplate} testid="newsletter-template"
            options={[{ value: "editorial", label: "Editorial (dark header)" }, { value: "announcement", label: "Announcement (gold header)" }, { value: "minimal", label: "Minimal (clean)" }, { value: "note", label: "Personal note" }]} />
          <p className="text-xs text-espresso/50 leading-relaxed">Every send includes a one-click unsubscribe link, as required by law.</p>
        </Card>

        <Card className="h-fit">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={15} className="text-gold" />
            <p className="text-[0.68rem] tracking-[0.14em] uppercase text-espresso/60">Write with AI</p>
          </div>
          <Field label="What's it about?" value={aiPrompt} onChange={setAiPrompt} textarea placeholder="e.g. Announce that Ori Zuri, our hand-crocheted cobalt skirt suit, drops on 12 October — invite readers to join the waitlist." testid="newsletter-ai-prompt" />
          <Select label="Tone" value={tone} onChange={setTone} testid="newsletter-ai-tone"
            options={[{ value: "warm", label: "Warm & personal" }, { value: "formal", label: "Formal & precise" }, { value: "brief", label: "Short & punchy" }]} />
          <button type="button" onClick={draft} disabled={drafting} data-testid="newsletter-ai-draft"
            className="w-full flex items-center justify-center gap-2 text-[0.66rem] tracking-[0.14em] uppercase border border-espresso/40 px-3 py-2.5 hover:bg-espresso hover:text-cream transition-colors disabled:opacity-50">
            {drafting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft the message
          </button>
          <p className="text-[0.62rem] text-espresso/40 mt-2 leading-relaxed">The assistant only uses facts from your pages; anything it can't confirm is marked <em>[to confirm]</em> for you to fill in.</p>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterAdmin;
